import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Lock, Search } from "lucide-react";
import api from "@/lib/api";
import { openDocument } from "@/lib/documents";
import { StackedList, Pagination } from "@/components/shared/StackedList";
import { PageHeader, FilterBar } from "@/components/shared/PageChrome";
import { SearchInput, InfoBanner, useListParams } from "@/components/shared/ListChrome";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
const fmtSize = (b) => (b == null ? "—" : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`);
const kind = (d) => (/confirmation/i.test(d.title) ? "Confirmation" : d.content_type === "application/pdf" ? "PDF" : "Other");

export default function ClientDocuments() {
  const { q, page, setQ, setPage } = useListParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/client/documents", { params: { q: q || undefined, page, page_size: 25 } }).then((r) => setData(r.data)).catch(() => setError(true));
  }, [q, page]);

  if (error) return <ErrorState testId="client-documents-error" />;

  return (
    <div data-testid="client-documents-page" className="space-y-5">
      <PageHeader title="Documents" subtitle="Access booking confirmations, amendments and other important documents." />

      <FilterBar testId="client-documents-filter-bar" summary={data ? `${data.total} document${data.total === 1 ? "" : "s"}` : null}>
        <SearchInput testId="client-documents-search" value={q} onChange={setQ} placeholder="Search by title or booking reference…" />
      </FilterBar>

      {!data && <LoadingState testId="client-documents-loading" />}
      {data && data.items.length === 0 && <EmptyState testId="client-documents-empty" icon={FileText} title={q ? "No documents match" : "No documents yet"} description={q ? "Try a different title or booking reference." : "Confirmations issued for your bookings appear here."} />}
      {data && data.items.length > 0 && (
        <div className="space-y-3">
          <StackedList testId="client-documents-stacked" items={data.items} title={(d) => d.title} onOpen={(d) => openDocument(d.id)} openLabel="Open document"
            fields={[{ label: "Booking", value: (d) => d.booking_reference, mono: true }, { label: "Issued", value: (d) => fmtDate(d.created_at) }, { label: "Type", value: kind }, { label: "Size", value: (d) => fmtSize(d.size_bytes) }]} />
          <div className="hidden sm:block">
          <Table data-testid="client-documents-table">
            <TableHeader>
              <TableRow><TableHead>Document</TableHead><TableHead>Booking</TableHead><TableHead>Issued</TableHead><TableHead>Type</TableHead><TableHead>Size</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((d) => (
                <TableRow key={d.id} data-testid={`client-document-row-${d.id}`}>
                  <TableCell>
                    <span className="flex items-center gap-2.5">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-jigsaw-orange/10 text-jigsaw-orange"><FileText className="h-4 w-4" /></span>
                      <span className="font-medium text-jigsaw-navy">{d.title}</span>
                      <Lock className="h-3.5 w-3.5 text-jigsaw-muted" title="Private, authenticated document access" />
                    </span>
                  </TableCell>
                  <TableCell>{d.booking_reference ? <button type="button" data-testid={`client-document-booking-${d.id}`} onClick={() => navigate(`/client/bookings/${d.booking_id}`)} className="font-mono-ref text-[13px] text-jigsaw-navy hover:underline">{d.booking_reference}</button> : "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-[13px]">{fmtDate(d.created_at)}</TableCell>
                  <TableCell>{kind(d)}</TableCell>
                  <TableCell className="text-[13px] text-jigsaw-muted">{fmtSize(d.size_bytes)}</TableCell>
                  <TableCell className="text-right"><button type="button" data-testid={`client-document-open-${d.id}`} onClick={() => openDocument(d.id)} className="text-[13px] font-medium text-[#1F4E9C] underline-offset-2 hover:underline">Download</button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <Pagination testId="client-documents-pagination" page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />
        </div>
      )}

      <InfoBanner testId="client-documents-banner" icon={Search} title="Looking for something specific?" text="Every document is linked to its booking - open the booking to see all revisions, or ask the Jigsaw team." actionLabel="Contact Jigsaw" to="/client/support" />
    </div>
  );
}
